import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { A11yService } from './a11y.service';
import { NotificationService } from './notification.service';
import { TranslateService } from '../translate/translate.service';
import { NotificationPosition } from '../models/notification.model';
import { IGlobalError } from '../../../shared/interfaces/error.interface';
import { errorMessagesNames } from '../../../shared/constants/errors';
import { IMobileError } from '../../../shared/interfaces/mobile-app.interface';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { mobileErrorTranslationKey } from '../constants/mobile-app.constants';
import { MobileErrorCode } from '../../../shared/constants/mobile-app';

@Injectable({
  providedIn: 'root',
})
export class MobileErrorHandler {
  private readonly unknownMobileErrorMessage: string = 'device_api_error_unknown';

  constructor(
    private a11yService: A11yService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {}

  public handleError(
    baseMessage: string,
    error: IMobileError,
    componentName: string,
    keepAfterRouteChange: boolean = false,
  ): void {
    const message: string = `${this.translateService.instant(baseMessage)}: ${this.translateMobileError(error)}`;

    this.a11yService.setMessage(message);
    this.notificationService.error(message, NotificationPosition.Toast, keepAfterRouteChange);

    console.error(`[${componentName}.handleError]`, message, error);
  }

  public handleResponse(
    baseMessage: string,
    response: HttpErrorResponse,
    componentName: string,
    keepAfterRouteChange: boolean = false,
  ): void {
    let message: string = this.translateService.instant(baseMessage);

    const mobileErrorMessage: string = this.getMobileErrorMessage(response);
    if (SharedCommonUtility.notNullish(mobileErrorMessage)) {
      message = `${message}: ${mobileErrorMessage}`;
    }

    this.a11yService.setMessage(message);
    this.notificationService.error(message, NotificationPosition.Toast, keepAfterRouteChange);

    console.error(`[${componentName}.handleResponse]`, message, response);
  }

  public handleResponse$(
    baseMessage: string,
    response: HttpErrorResponse,
    componentName: string,
    keepAfterRouteChange: boolean = false,
  ): Observable<never> {
    this.handleResponse(baseMessage, response, componentName, keepAfterRouteChange);
    return EMPTY;
  }

  public getMobileErrorMessage(
    response: HttpErrorResponse,
    options?: { whitelist?: MobileErrorCode[]; noDefaultTranslation?: boolean },
  ): string | null {
    const globalError: IGlobalError = response.error;
    if (globalError?.app?.name !== errorMessagesNames.MobileAppsIntegrationError) {
      return null;
    }

    return this.translateMobileError(globalError.error, options);
  }

  public translateMobileError(
    error: IMobileError,
    options?: { whitelist?: MobileErrorCode[]; noDefaultTranslation?: boolean },
  ): string {
    if (
      error?.code in mobileErrorTranslationKey &&
      (SharedCommonUtility.isNullish(options?.whitelist) || options.whitelist.includes(error.code))
    ) {
      return this.translateService.instant(mobileErrorTranslationKey[error.code], error.params);
    }

    if (options?.noDefaultTranslation) {
      return null;
    }

    return this.translateService.instant(this.unknownMobileErrorMessage);
  }
}
